import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
  EyeIcon,
  PencilSquareIcon,
  SquaresPlusIcon,
} from '@heroicons/react/24/outline';
import { Send } from 'lucide-react';

import Dash from '@dashboard/components/Dash';
import EmailCustomerDialog from '@dashboard/components/EmailCustomerDialog';
import FieldEditDialog from '@dashboard/components/FieldEditDialog';
import Link from '@dashboard/components/Link';
import {
  Action,
  ActionGroup,
  ItemGroup,
  ItemRow,
  OrderAbandonmentCard,
  OrderActionsColumn,
  OrderCustomerCard,
  OrderFieldsCard,
  OrderLicenseKeyCard,
  OrderPaymentBlock,
  OrderQueryParametersCard,
  OrderTableCard,
  sortDetailRows,
  SubscriptionAlerts,
  TotalsList,
  actionClass,
  joinDetails,
  stripeIdRow,
  hasPrimarySetupFeeLine,
  nestSetupFees,
  setupFeeSubLine,
} from '@dashboard/components/OrderPage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import useLicenseKeyQueries from '@dashboard/hooks/queries/useLicenseKeyQueries';
import { buildEmailVariables } from '@dashboard/lib/emailVariables';
import { formatDateTime } from '@dashboard/lib/format-date';
import { confirm } from '@dashboard/lib/confirm';
import { subscriptionEmailLogsKey, subscriptionKey } from '@dashboard/lib/queryKeys';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import isAbandonedAbandonment from '@dashboard/services/abandonment/isAbandonedAbandonment';
import { formatPrice } from '@dashboard/services/formatting';
import getPageTypeLabel, { getPageTypeHeading } from '@dashboard/services/getPageTypeLabel';
import { fetcher } from '@dashboard/services/request';
import { getPaymentMethodLabel } from '@repo/lib';
import PaymentsSubscriptionsCancelDialog from '../components/PaymentsSubscriptionsCancelDialog';
import {
  getCouponDurationLabel,
  getPaymentTypeLabel,
  getSetupFeeLines,
  getSubscriptionTotals,
  getVariantRows,
  hasOrderItems,
  moneyLabel,
  recurringLabel,
} from '../subscriptionTotals';
import { usePageRecordHref } from '@dashboard/lib/detailLinks';
import { formatOrderId } from '@dashboard/lib/orderId';

const STRIPE_TOOLTIP = 'Only for live subscriptions';

/**
 * An order item as a row. Its setup fee line, when the order has one, sits under it: the primary
 * product's fee carries the Setup fee row's discount, fees and total lines, a bump's fee is one line.
 */
const itemRow = ({ item, setupFee }, { price, money, setupLines, subscription }) => {
  const recurring = item.billingType === 'recurring';
  const ownsSetupFeeRow =
    setupFee && !item.isOrderBump && setupLines.length && subscription.setupFee === setupFee.amount;
  return {
    name: item.name,
    imageUrl: item.imageUrl,
    details: `${item.quantity} × ${price(item.unitPrice, recurring)}`,
    // item.amount includes the variant options, one-time ones too, which have their own rows.
    amount: price(item.unitPrice * item.quantity, recurring),
    sub: setupFee ? (ownsSetupFeeRow ? setupLines : [setupFeeSubLine(setupFee, money)]) : null,
  };
};

/** Product, Variants and Order bumps rows with the interval on recurring amounts, the setup fee's lines under the product, then the recurring totals. */
export const SubscriptionItems = ({ subscription }) => {
  const money = amount => moneyLabel(subscription, amount);
  const perInterval = amount => recurringLabel(subscription, amount);
  const price = (amount, recurring) => (recurring ? perInterval(amount) : money(amount));
  const items = subscription.orderItems ?? [];
  const nested = nestSetupFees(items);
  const setupLines = getSetupFeeLines(subscription);
  const rowOptions = { price, money, setupLines, subscription };
  const legacy = !hasOrderItems(subscription);
  const showProductPrice = legacy && subscription.productPrice && !subscription.dynamicPrice;
  const products = legacy
    ? [
        {
          name: subscription.page?.productTitle || <Dash />,
          details: showProductPrice ? 'Product price' : null,
          amount: showProductPrice ? perInterval(subscription.productPrice) : null,
        },
      ]
    : nested.filter(row => !row.item.isOrderBump).map(row => itemRow(row, rowOptions));
  const bumps = nested.filter(row => row.item.isOrderBump).map(row => itemRow(row, rowOptions));
  // The record's own Setup fee lines sit under the product only when no order line carries the fee.
  const hasSetupFeeItem = hasPrimarySetupFeeLine(items);
  const variants = getVariantRows(subscription);
  const hasImages = items.some(item => item.imageUrl);
  const href = subscription.page?._id ? `/pages/${subscription.page._id}` : undefined;

  const { rows } = getSubscriptionTotals(subscription);
  const totals = rows.map(row => {
    let value;
    if (row.kind === 'discounted-from')
      value = <span className="line-through">{perInterval(row.amount)}</span>;
    else if (row.kind === 'fee') value = perInterval(row.amount);
    else if (row.kind === 'coupon') value = row.amount < 0 ? `-${money(-row.amount)}` : '';
    else if (row.kind === 'billing') value = '';
    else value = row.recurring ? perInterval(row.amount) : money(row.amount);
    let label = row.label;
    if (row.kind === 'coupon') {
      const { coupon } = subscription;
      const duration = getCouponDurationLabel(coupon);
      label = (
        <>
          Discount: {coupon.label} (<Link to={`/coupons/${coupon._id}`}>{coupon.code}</Link>
          {coupon.percentOff ? `, ${coupon.percentOff}% off` : ''}
          {duration ? `, ${duration}` : ''})
        </>
      );
    }
    if (row.kind === 'billing' && row.plan)
      label = <>Payment plan: {row.label.charAt(0).toLowerCase() + row.label.slice(1)}</>;
    return { key: row.key, label, value, bold: row.bold, rule: row.rule };
  });

  return (
    <>
      <ItemGroup title={products.length > 1 ? 'Products' : 'Product'}>
        {products.map((product, index) => (
          <ItemRow
            key={index}
            showImage={hasImages}
            imageUrl={product.imageUrl}
            name={product.name}
            href={href}
            details={product.details}
            amount={product.amount}
            sub={!hasSetupFeeItem && index === 0 && setupLines.length ? setupLines : product.sub}
          />
        ))}
      </ItemGroup>
      {variants.length > 0 && (
        <ItemGroup title="Variants">
          {variants.map(variant => (
            <ItemRow
              key={variant.key}
              showImage={hasImages}
              name={variant.name}
              details={joinDetails([
                `${variant.quantity} × ${price(variant.unitPrice, variant.recurring)}`,
                variant.payWhatYouWant ? 'Pay what you want' : null,
              ])}
              amount={price(variant.amount, variant.recurring)}
            />
          ))}
        </ItemGroup>
      )}
      {bumps.length > 0 && (
        <ItemGroup title="Order bumps">
          {bumps.map((item, index) => (
            <ItemRow
              key={index}
              showImage={hasImages}
              imageUrl={item.imageUrl}
              name={item.name}
              href={href}
              details={item.details}
              amount={item.amount}
              sub={item.sub}
            />
          ))}
        </ItemGroup>
      )}
      <TotalsList totals={totals} />
    </>
  );
};

const PaymentsSubscriptionsDetail = ({ subscription, refetch }) => {
  const recordHref = usePageRecordHref();
  const { seller, isAdminLoggedInAsSeller, token } = useAuth();
  const { toggleLicenseKey } = useLicenseKeyQueries();
  const [isTogglingLicenseKey, setIsTogglingLicenseKey] = useState(false);
  const queryClient = useQueryClient();
  const { currency } = subscription;

  const { mutateAsync: updateFields } = useMutation({
    mutationFn: async values =>
      fetcher(`/sellers/${seller._id}/subscriptions/${subscription._id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ fields: values }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKey(subscription._id) });
      toast({ title: 'Fields updated' });
    },
  });

  const { mutate: sendConfirmationEmail, isPending: isSendingConfirmationEmail } = useMutation({
    mutationFn: async () =>
      fetcher(
        `/sellers/${seller._id}/subscriptions/${subscription._id}/send-confirmation-email`,
        token,
        { method: 'POST' },
      ),
    onSuccess: () => toast({ title: 'Confirmation email resent' }),
    onError: error =>
      toast({
        title: 'Failed to send confirmation email',
        description: error?.message,
        type: 'error',
      }),
  });

  const handleSendConfirmationEmail = async () => {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });
    if (confirmed) sendConfirmationEmail();
  };

  async function handleToggleLicenseKey() {
    setIsTogglingLicenseKey(true);
    const action = subscription.licenseKey.enabled ? 'disable' : 'enable';
    const { error } = await toggleLicenseKey(subscription.licenseKey._id, action);
    if (error) {
      setIsTogglingLicenseKey(false);
      window.alert(`Failed to ${action} license key. Try again or contact support.`);
      return;
    }
    refetch();
    setIsTogglingLicenseKey(false);
  }

  const stripe = {
    livemode: subscription.livemode,
    seller,
    isAdminLoggedInAsSeller,
    tooltip: STRIPE_TOOLTIP,
  };
  const details = [
    { label: 'Order ID', value: formatOrderId(subscription.orderId), copy: subscription.orderId },
    {
      label: 'Payment method',
      value: subscription.paymentMethod ? (
        getPaymentMethodLabel(subscription.paymentMethod, { includeBrand: false })
      ) : (
        <Dash />
      ),
      copy: subscription.paymentMethod
        ? getPaymentMethodLabel(subscription.paymentMethod, { includeBrand: false })
        : null,
    },
    { label: 'SKU', value: subscription.sku || <Dash />, copy: subscription.sku || null },
    ...(subscription.feeAmount
      ? [
          {
            label: 'Checkout Page fee',
            value: formatPrice({ amount: subscription.feeAmount, currency }),
          },
        ]
      : []),
    {
      label: 'Customer ID',
      value: subscription.customer ? `#${subscription.customer._id}` : <Dash />,
      copy: subscription.customer ? `${subscription.customer._id}` : null,
    },
    { label: 'Subscription ID', value: `#${subscription._id}`, copy: `${subscription._id}` },
    stripeIdRow({
      label: 'Stripe customer ID',
      id: subscription.customer?.stripeCustomerId,
      path: `/customers/${subscription.customer?.stripeCustomerId}`,
      ...stripe,
    }),
    stripeIdRow({
      label: 'Stripe subscription ID',
      id: subscription.subscriptionId,
      path: `/subscriptions/${subscription.subscriptionId}`,
      ...stripe,
    }),
    { label: 'Payment type', value: getPaymentTypeLabel(subscription) },
    {
      label: 'Prefilled',
      value: subscription.isPrefilled ? 'Is prefilled' : '—',
      copy: subscription.isPrefilled ? 'Is prefilled' : null,
    },
    { label: 'Created', value: formatDateTime(subscription.createdAt) },
  ];

  const stripeOff = !subscription.livemode || !subscription.subscriptionId;
  const resendOff =
    isAbandonedAbandonment(subscription) ||
    subscription.status === 'failed' ||
    subscription.status === 'incomplete';
  const resendTitle = isAbandonedAbandonment(subscription)
    ? 'This checkout was abandoned'
    : subscription.status === 'failed'
      ? 'This subscription failed'
      : 'This subscription is incomplete';

  return (
    <>
      <SubscriptionAlerts subscription={subscription} />

      <div className="flex flex-col lg:flex-row max-w-5xl items-start justify-center">
        <div className="order-2 lg:order-1 w-full lg:pr-8">
          <OrderPaymentBlock>
            <SubscriptionItems subscription={subscription} />
          </OrderPaymentBlock>
          <OrderAbandonmentCard record={subscription} seller={seller} />
          <OrderLicenseKeyCard
            licenseKey={subscription.licenseKey}
            onToggle={handleToggleLicenseKey}
            toggling={isTogglingLicenseKey}
          />
          <OrderCustomerCard customer={subscription.customer} />
          <OrderFieldsCard
            fields={subscription.fields}
            page={subscription.page}
            updateFields={updateFields}
            groupAddresses={false}
          />
          <OrderTableCard title="Details" rows={sortDetailRows(details)} />
          <OrderQueryParametersCard queryParameters={subscription.queryParameters} />
        </div>

        <div className="order-1 lg:order-2 w-full lg:w-72 shrink-0 px-4 pt-3 lg:pt-[52px]">
          <OrderActionsColumn>
            <ActionGroup title="Subscription">
              <PaymentsSubscriptionsCancelDialog subscription={subscription} />
              <Action
                icon={ArrowTopRightOnSquareIcon}
                label="View in Stripe"
                to={getStripeDashboardUrl(`/subscriptions/${subscription.subscriptionId}`, {
                  seller,
                  isAdminLoggedInAsSeller,
                })}
                blank
                off={stripeOff}
                title={STRIPE_TOOLTIP}
              />
            </ActionGroup>
            <ActionGroup title="Email">
              <Action
                icon={EnvelopeIcon}
                label="Resend confirmation"
                onClick={handleSendConfirmationEmail}
                off={resendOff}
                title={resendTitle}
                loading={isSendingConfirmationEmail}
              />
              {subscription.customer?.email ? (
                <EmailCustomerDialog
                  customerEmail={subscription.customer.email}
                  livemode={subscription.livemode}
                  objectLabel="subscriptions"
                  endpoint={`/sellers/${seller._id}/subscriptions/${subscription._id}/send-email`}
                  variables={buildEmailVariables({ page: subscription.page })}
                  queryKey={subscriptionEmailLogsKey(subscription._id)}
                  emailsPath={`/subscriptions/${subscription._id}/emails`}
                />
              ) : (
                <Action
                  icon={Send}
                  label="Email customer"
                  off
                  title="The customer has no email address"
                />
              )}
            </ActionGroup>
            <ActionGroup title="Customer">
              <Action
                icon={EyeIcon}
                label="View customer"
                to={
                  subscription.customer ? recordHref('customers', subscription.customer._id) : '#'
                }
                off={!subscription.customer}
                title="No customer on this subscription"
              />
            </ActionGroup>
            <ActionGroup title="Fields">
              <FieldEditDialog
                fields={subscription.fields}
                page={subscription.page}
                updateFields={updateFields}
                trigger={
                  <Link className={actionClass(false, 'cursor-pointer')} onClick={() => {}}>
                    <>
                      <PencilSquareIcon className="w-4 h-4" />
                      Edit fields
                    </>
                  </Link>
                }
              />
            </ActionGroup>
            <ActionGroup title={getPageTypeHeading(subscription.page)}>
              <Action
                icon={SquaresPlusIcon}
                label={`View ${getPageTypeLabel(subscription.page)}`}
                to={subscription.page?._id ? `/pages/${subscription.page._id}` : '#'}
                off={!subscription.page?._id}
                title="This subscription has no page"
              />
            </ActionGroup>
          </OrderActionsColumn>
        </div>
      </div>
    </>
  );
};

export default PaymentsSubscriptionsDetail;
